import { PaginatedResponse, WithListParams } from '@/types/pagination'
import {
  AlertPrompt,
  MilestoneServer,
  MilestoneServerCamera,
  Pipeline,
  PipelineSourceWrite,
  VlmAction,
  VlmModel,
  VlmResult
} from '@/types/vlm'
import { vpApi } from './vpApi'

export type CreatePipelineData = {
  name: string
  device_id: string
  source: PipelineSourceWrite
  user_prompt?: string
  alert_prompts: AlertPrompt[]
  actions: VlmAction[]
  enable_thinking: boolean
  enable_cloud_results: boolean
  fps: number
}

export const getVlmModelList = async (params: WithListParams) => {
  const res = await vpApi.get<PaginatedResponse<VlmModel[]>>(
    '/v1/devices/vlm-model-profiles/',
    { params }
  )

  return res.data
}

export const getVlmPipelineList = async (params: WithListParams) => {
  const res = await vpApi.get<PaginatedResponse<Pipeline[]>>(
    '/v1/devices/vlm-pipelines/',
    { params }
  )

  return res.data
}

export const createVlmPipeline = async (data: CreatePipelineData) => {
  const res = await vpApi.post<Pipeline>('/v1/devices/vlm-pipelines/', data)
  return res.data
}

export type UpdatePipelineData = {
  pipelineId: string
  data: Partial<CreatePipelineData>
}

export const updateVlmPipeline = async ({
  pipelineId,
  data
}: UpdatePipelineData) => {
  const res = await vpApi.patch<Pipeline>(
    `/v1/devices/vlm-pipelines/${pipelineId}/`,
    data
  )
  return res.data
}

export const deleteVlmPipeline = async (pipelineId: string) => {
  await vpApi.delete(`/v1/devices/vlm-pipelines/${pipelineId}/`)
}

export const restartVlmPipeline = async (pipelineId: string) => {
  await vpApi.post(`/v1/devices/vlm-pipelines/${pipelineId}/restart/`)
}

export type GetVlmResultsParams = WithListParams<{
  start_date?: string
  end_date?: string
}>

export const getVlmResultList = async (params: GetVlmResultsParams) => {
  const res = await vpApi.get<PaginatedResponse<VlmResult[]>>(
    '/v1/devices/vlm-events/',
    { params }
  )

  return res.data
}

// The list response carries no video_url — it would expire in the row.
export const getVlmResult = async (resultId: string) => {
  const res = await vpApi.get<VlmResult>(`/v1/devices/vlm-events/${resultId}/`)

  return res.data
}

export const getMilestoneServerList = async (params: WithListParams) => {
  const res = await vpApi.get<PaginatedResponse<MilestoneServer[]>>(
    '/v1/devices/milestone-servers/',
    { params }
  )

  return res.data
}

type CreateMilestoneServerData = {
  name: string
  server_url: string
  username: string
  password: string
}

export const createMilestoneServer = async (
  data: CreateMilestoneServerData
) => {
  const res = await vpApi.post<MilestoneServer>(
    '/v1/devices/milestone-servers/',
    data
  )
  return res.data
}

export const deleteMilestoneServer = async (serverId: string) => {
  await vpApi.delete(`/v1/devices/milestone-servers/${serverId}/`)
}

export type TestMilestoneConnectionParams = {
  deviceId: string
  serverUrl: string
  username: string
  password: string
}

type TestMilestoneConnectionResponse = {
  task_id: string
}

export const testMilestoneConnection = async ({
  deviceId,
  serverUrl,
  username,
  password
}: TestMilestoneConnectionParams) => {
  const res = await vpApi.post<TestMilestoneConnectionResponse>(
    `/v1/devices/milestone-servers/${deviceId}/test-connection/`,
    {
      server_url: serverUrl,
      username,
      password
    }
  )
  return res.data
}

type ListMilestoneServerCamerasResponse = {
  cameras: MilestoneServerCamera[]
}

// Synchronous device RPC through ThingsBoard; can take up to ~30 s.
export const listMilestoneServerCameras = async (
  serverId: string,
  deviceId: string
) => {
  const res = await vpApi.post<ListMilestoneServerCamerasResponse>(
    `/v1/devices/milestone-servers/${serverId}/cameras/`,
    { device_id: deviceId }
  )
  return res.data
}

export const vlmApi = {
  getVlmModelList,
  getVlmPipelineList,
  createVlmPipeline,
  updateVlmPipeline,
  deleteVlmPipeline,
  restartVlmPipeline,
  getVlmResultList,
  getVlmResult,
  getMilestoneServerList,
  createMilestoneServer,
  deleteMilestoneServer,
  testMilestoneConnection,
  listMilestoneServerCameras
}
